import { useState, useEffect, useRef } from 'react';
import { useUi } from '../i18n/uiTranslations';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { serviceTranslations } from '../i18n/serviceTranslations';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { AnimatedCounter } from '../components/Animations';
import { SERVICE_VISUALS } from '../components/ServiceVisuals';
import { ServiceScene3D } from '../components/ServiceScene3D';
import { useSceneNav } from '../components/SceneTransition';
import { gsap, MOTION_ANY, scrollToId } from '../lib/gsap';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPECIAL = ['/', 'Weeks', 'Hafta', 'Wochen', 'Semaines', 'Semanas', 'Недел', 'Εβδομάδ', 'min', 'dk', 'мин'];
const shouldAnimate = (v) => !/\d+[.,]\d+/.test(v) && !SPECIAL.some((s) => v.includes(s));

const PARTNER_SUBTITLE = { aggregator: 'subAggregator', platform: 'subPlatform', payments: 'subPayments', crypto: 'subCrypto' };

const SectionHead = ({ index, title, sub }) => (
  <div>
    <p className="eyebrow">{index}</p>
    <h2 className="display-xl mt-6 text-[clamp(2.2rem,4.4vw,4.2rem)]">{title}</h2>
    {sub && <p className="mt-6 text-base sm:text-lg text-[#ABA59A] max-w-[440px] leading-relaxed">{sub}</p>}
  </div>
);

const PartnerRow = ({ items, reverse, className, speed }) => (
  <div className="overflow-hidden mask-fade-x">
    <div className={`marquee-track ${reverse ? 'marquee-reverse' : ''}`} style={{ '--marquee-speed': speed }}>
      {[0, 1].map((k) => (
        <div key={k} className="flex items-center shrink-0" aria-hidden={k === 1}>
          {items.map((p, i) => (
            <span key={`${k}-${i}`} className={`flex items-center whitespace-nowrap ${className}`}>
              {p}<span className="mx-8 lg:mx-12 w-1.5 h-1.5 rotate-45 bg-[#C8AA73]/60" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const ServicePage = ({ serviceKey }) => {
  const { language } = useLanguage();
  const u = useUi();
  const navigate = useNavigate();
  const sceneGo = useSceneNav();
  const root = useRef(null);
  const [loading, setLoading] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });

  const t = serviceTranslations[language] || serviceTranslations.en;
  const service = t[serviceKey];
  const common = t.common;
  const Visual = SERVICE_VISUALS[serviceKey];
  const half = Math.ceil(service.partners.length / 2);

  useEffect(() => {
    const mm = gsap.matchMedia();
    mm.add(MOTION_ANY, () => {
      const ctx = gsap.context(() => {
        gsap.fromTo('.steps-fill', { scaleX: 0, scaleY: 0 }, {
          scaleX: 1, scaleY: 1, ease: 'none',
          scrollTrigger: { trigger: '.steps-list', start: 'top 75%', end: 'bottom 55%', scrub: true },
        });
        gsap.to('.svc-visual', {
          yPercent: 10, ease: 'none',
          scrollTrigger: { trigger: '.svc-hero', start: 'top top', end: 'bottom top', scrub: true },
        });
      }, root);
      return () => ctx.revert();
    });
    return () => mm.revert();
  }, [serviceKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...contactForm, company: `Interest: ${service.hero.title}`, language });
      toast.success(common.sent);
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error(u('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoClick = () => navigate('/?demo=true');
  const setF = (k) => (e) => setContactForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div ref={root} className="min-h-screen bg-[#080808]" data-testid={`service-page-${serviceKey}`}>
      <Header onDemoClick={handleDemoClick} />

      <section className="svc-hero relative overflow-hidden px-6 lg:px-12 pt-32 lg:pt-40 pb-16" style={{ background: 'radial-gradient(70% 60% at 80% 35%, rgba(200,170,115,0.07), transparent 70%)' }}>
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <button onClick={() => sceneGo('/', null, { back: true })} className="group inline-flex items-center gap-2 text-sm text-[#ABA59A] hover:text-[#F4F0E8] transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" strokeWidth={1.5} />
              {common.backToHome}
            </button>
            <p className="eyebrow mt-14 rise-in">{service.hero.label}</p>
            <h1 className="display-xl mt-8 text-[clamp(3rem,7.4vw,7.2rem)] break-words rise-in" style={{ animationDelay: '0.12s' }} data-testid="service-title">
              {service.hero.title}
            </h1>
            <p className="mt-8 max-w-[580px] text-base sm:text-lg leading-relaxed text-[#ABA59A] rise-in" style={{ animationDelay: '0.25s' }}>{service.hero.subtitle}</p>
            <div className="mt-12 flex flex-wrap gap-4 rise-in" style={{ animationDelay: '0.38s' }}>
              <button onClick={handleDemoClick} className="btn-primary" data-testid="service-demo-btn">{common.requestDemo}</button>
              <button onClick={() => scrollToId('contact-form')} className="btn-secondary" data-testid="service-contact-btn">{common.contactUs}</button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="svc-visual -mx-2 sm:mx-auto w-[calc(100%+1rem)] sm:w-full max-w-[560px] aspect-square">
              <ServiceScene3D kind={serviceKey} Fallback={Visual} />
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto mt-16 lg:mt-24">
          <p className="font-mono-lux text-[0.7rem] tracking-[0.16em] uppercase text-[#7C776E] mb-6">{common.stats}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-[#F4F0E8]/10">
            {service.stats.map((stat, i) => (
              <div key={i} className={`py-10 ${i % 2 ? 'pl-6 border-l' : 'pr-4'} lg:pl-8 lg:border-l lg:first:pl-0 lg:first:border-l-0 border-[#F4F0E8]/10 ${i > 1 ? 'border-t lg:border-t-0' : ''}`} data-testid={`stat-${i}`}>
                <div className="display-xl text-[clamp(2.4rem,4.6vw,4.4rem)]">
                  {shouldAnimate(stat.value) ? <AnimatedCounter value={stat.value} duration={2} /> : stat.value}
                </div>
                <div className="mt-3 text-sm text-[#ABA59A]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative px-6 lg:px-12 py-28 lg:py-40 bg-[#0c0c0b] border-t border-[#F4F0E8]/[0.06]">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4"><div className="lg:sticky lg:top-32"><SectionHead index="01" title={common.features} /></div></div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-12">
            {service.features.map((f, i) => (
              <article key={i} className="group py-10 border-t border-[#F4F0E8]/[0.08] hover:border-[#C8AA73]/50 transition-colors duration-500" data-testid={`feature-${i}`}>
                <span className="font-mono-lux text-[0.7rem] text-[#C8AA73]">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-display text-2xl text-[#F4F0E8] mt-4 leading-snug group-hover:text-[#E8D6AF] transition-colors">{f.title}</h3>
                <p className="mt-4 text-[0.95rem] leading-relaxed text-[#ABA59A]">{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-28 lg:py-40 border-t border-[#F4F0E8]/[0.06] overflow-hidden" data-testid="partners-section">
        <div className="px-6 lg:px-12 max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6"><SectionHead index="02" title={common.partners} sub={u(PARTNER_SUBTITLE[serviceKey])} /></div>
          <div className="lg:col-span-6 flex lg:justify-end items-end">
            <dl className="flex border-t border-[#F4F0E8]/10 w-full lg:w-auto">
              {[
                [`${service.partners.length}+`, u('partners')],
                [serviceKey === 'aggregator' ? '10K+' : serviceKey === 'crypto' ? '50+' : '100+', u(serviceKey === 'aggregator' ? 'games' : serviceKey === 'crypto' ? 'coins' : 'integrations')],
                ['24/7', u('support')],
              ].map(([v, l], i) => (
                <div key={l} className={`pt-6 pr-4 sm:pr-8 ${i ? 'pl-4 sm:pl-8 border-l border-[#F4F0E8]/10' : ''}`}>
                  <dt className="font-display text-3xl sm:text-4xl text-[#F4F0E8]">{v}</dt>
                  <dd className="mt-1 text-xs text-[#7C776E]">{l}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <PartnerRow items={service.partners.slice(0, half)} speed="70s" className="font-display text-[clamp(1.8rem,3.4vw,3.2rem)] text-[#F4F0E8]/85" />
        <div className="mt-6">
          <PartnerRow items={service.partners.slice(half).length ? service.partners.slice(half) : service.partners} reverse speed="80s" className="font-display italic text-[clamp(1.8rem,3.4vw,3.2rem)] text-[#C8AA73]/80" />
        </div>
        <div className="px-6 lg:px-12 max-w-[1440px] mx-auto mt-16">
          <ul className="columns-2 sm:columns-3 lg:columns-5 gap-8 border-t border-[#F4F0E8]/[0.07] pt-8" data-testid="partners-list">
            {service.partners.map((p) => (
              <li key={p} className="break-inside-avoid py-1.5 text-sm text-[#ABA59A] hover:text-[#F4F0E8] transition-colors">{p}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="relative px-6 lg:px-12 py-28 lg:py-40 bg-[#0c0c0b] border-t border-[#F4F0E8]/[0.06]">
        <div className="max-w-[1440px] mx-auto">
          <SectionHead index="03" title={common.integration} />
          <ol className="steps-list relative mt-16 lg:mt-24 grid lg:grid-cols-4 gap-0 lg:gap-10 pl-8 lg:pl-0">
            <span className="absolute left-0 top-0 bottom-0 w-px lg:w-auto lg:h-px lg:right-0 lg:bottom-auto bg-[#F4F0E8]/10" />
            <span className="steps-fill absolute left-0 top-0 bottom-0 w-px lg:w-auto lg:h-px lg:right-0 lg:bottom-auto bg-[#C8AA73] origin-top lg:origin-left" />
            {service.integration.map((step, i) => (
              <li key={i} className="relative py-8 lg:pt-14 lg:py-0" data-testid={`integration-step-${i}`}>
                <span className="absolute -left-8 lg:left-0 top-10 lg:-top-[5px] -translate-x-1/2 lg:translate-x-0 w-2.5 h-2.5 rotate-45 bg-[#080808] border border-[#C8AA73]" />
                <span className="font-display text-5xl text-[#C8AA73]/90">{step.step}</span>
                <h4 className="font-display text-2xl text-[#F4F0E8] mt-6">{step.title}</h4>
                <p className="mt-4 text-[0.95rem] leading-relaxed text-[#ABA59A]">{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="relative px-6 lg:px-12 py-28 lg:py-40 border-t border-[#F4F0E8]/[0.06]">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><SectionHead index="04" title={common.faq} /></div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="border-t border-[#F4F0E8]/10">
              {service.faq.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#F4F0E8]/10" data-testid={`faq-${i}`}>
                  <AccordionTrigger className="py-7 text-left font-display text-xl sm:text-2xl text-[#F4F0E8] hover:no-underline hover:text-[#E8D6AF] data-[state=open]:text-[#E8D6AF] [&>svg]:text-[#C8AA73] transition-colors">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 text-base leading-relaxed text-[#ABA59A] max-w-[680px]">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section id="contact-form" className="relative px-6 lg:px-12 py-28 lg:py-40 bg-[#0c0c0b] border-t border-[#F4F0E8]/[0.06]">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionHead index="05" title={common.quickContact} />
            <div className="mt-14 pt-10 border-t border-[#F4F0E8]/10">
              <h3 className="font-display text-3xl text-[#F4F0E8]">{u('readyTitle')}</h3>
              <p className="mt-4 text-[#ABA59A] leading-relaxed max-w-[420px]">{u('readyDesc')}</p>
              <button onClick={handleDemoClick} className="btn-secondary mt-8 group" data-testid="cta-demo-btn">
                {common.requestDemo}
                <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
              </button>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="lg:col-span-7 rounded-[28px] metal-edge bg-[#111110] p-6 sm:p-10 lg:p-14 space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <label className="block">
                <span className="field-label">{common.name}</span>
                <Input value={contactForm.name} onChange={setF('name')} className="input-dark" placeholder="John Doe" required data-testid="quick-contact-name" />
              </label>
              <label className="block">
                <span className="field-label">{common.email}</span>
                <Input type="email" value={contactForm.email} onChange={setF('email')} className="input-dark" placeholder="john@company.com" required data-testid="quick-contact-email" />
              </label>
            </div>
            <label className="block">
              <span className="field-label">{common.message}</span>
              <Textarea value={contactForm.message} onChange={setF('message')} className="input-dark min-h-[140px] resize-none" placeholder={u('phMessage')} required data-testid="quick-contact-message" />
            </label>
            <button type="submit" className="btn-primary" disabled={loading} data-testid="quick-contact-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? common.sending : common.sendInquiry}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};
